import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const TABS = [
  "Why PuranaWala",
  "Electronics",
  "Automobiles",
  "Fittings",
  "Anything",
  "Jugaadu",
  "Kabaadi",
  "AI",
];

const dummyResults = [
  {
    id: 1,
    item: "Laptop Charger",
    partNumber: "LC-4521",
    make: "Dell",
    model: "Inspiron 15",
    status: "Available",
  },
  {
    id: 2,
    item: "Mobile Display",
    partNumber: "MD-7782",
    make: "Samsung",
    model: "Galaxy A51",
    status: "Available",
  },
  {
    id: 3,
    item: "Bike Headlight",
    partNumber: "BH-9923",
    make: "Hero",
    model: "Splendor+",
    status: "Available",
  },
  {
    id: 4,
    item: "Water Tap Nozzle",
    partNumber: "WT-1291",
    make: "Jaquar",
    model: "PrimeFlow",
    status: "Available",
  },
  {
    id: 5,
    item: "TV Circuit Board",
    partNumber: "CB-5610",
    make: "Sony",
    model: "Bravia 40",
    status: "Available",
  },
  {
    id: 6,
    item: "AC Fan Motor",
    partNumber: "AF-8841",
    make: "LG",
    model: "DualCool",
    status: "Available",
  },
  {
    id: 7,
    item: "Car Side Mirror",
    partNumber: "CM-3249",
    make: "Maruti",
    model: "Swift",
    status: "Available",
  },
  {
    id: 8,
    item: "Gas Stove Knob",
    partNumber: "GK-1120",
    make: "Prestige",
    model: "Alpha",
    status: "Available",
  },
  {
    id: 9,
    item: "LED Bulb Driver",
    partNumber: "LD-5509",
    make: "Philips",
    model: "SmartBright",
    status: "Available",
  },
  {
    id: 10,
    item: "USB Charging Port",
    partNumber: "UP-9001",
    make: "Xiaomi",
    model: "Redmi 9",
    status: "Available",
  },
];

const jugaadList = [
  {
    id: 1,
    title: "Solar Fan Hack",
    date: "2024-02-10",
    comments:
      "Made from old motor + panel for low-cost cooling.",
    parts: ["Small Motor", "Fan Blade", "Solar Panel"],
  },
  {
    id: 2,
    title: "Bike Phone Charger",
    date: "2024-03-15",
    comments:
      "DC converter from bike battery to 5V USB.",
    parts: ["DC Converter", "USB Port", "Cable"],
  },
];

const scrapVendors = [
  {
    name: "Ravi",
    shop: "Ravi Scrap Center",
    city: "Mumbai",
    state: "Maharashtra",
    pin: "400001",
    country: "India",
    location: "Dadar",
  },
  {
    name: "Imran",
    shop: "Star Metal Scrap",
    city: "Pune",
    state: "Maharashtra",
    pin: "411001",
    country: "India",
    location: "Swargate",
  },
];

const pieData = [
  { name: "Electronics", value: 40 },
  { name: "Automobiles", value: 30 },
  { name: "Fittings", value: 20 },
  { name: "Others", value: 10 },
];

const COLORS = ["#60A5FA", "#34D399", "#FBBF24", "#F97373"];

const chartData = {
  labels: pieData.map((d) => d.name),
  datasets: [
    {
      label: "Typical Old Parts Demand Mix",
      data: pieData.map((d) => d.value),
      backgroundColor: COLORS,
      borderColor: COLORS,
      borderWidth: 1,
    },
  ],
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "50%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        color: "#9CA3AF",
        font: {
          size: 12,
        },
      },
    },
    tooltip: {
      backgroundColor: "#111827",
      titleColor: "#F9FAFB",
      bodyColor: "#F9FAFB",
      borderColor: "#4B5563",
      borderWidth: 1,
      cornerRadius: 8,
      displayColors: true,
      font: {
        size: 12,
      },
      callbacks: {
        label: function (context) {
          return `${context.label}: ${context.parsed}%`;
        },
      },
    },
  },
};

export default function PuranaWalaHome() {
  const [activeTab, setActiveTab] = useState("Why PuranaWala");
  const [search, setSearch] = useState("");
  const [vendorSearch, setVendorSearch] = useState("");
  const [selectedJugaad, setSelectedJugaad] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);

  const renderSearchResults = () => {
    const filtered = dummyResults.filter((item) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        item.item.toLowerCase().includes(q) ||
        item.partNumber.toLowerCase().includes(q) ||
        item.make.toLowerCase().includes(q) ||
        item.model.toLowerCase().includes(q)
      );
    });

    return (
      <div className="grid gap-3 mt-4">
        {filtered.map((item) => (
          <div
            key={item.id}
            className="bg-neutral-900 text-gray-200 rounded-2xl shadow-lg border border-neutral-800"
          >
            <div className="p-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-base lg:text-lg font-semibold text-white">
                  {item.item}
                </h3>
                <span className="text-xs lg:text-sm text-green-400 font-bold">
                  {item.status}
                </span>
              </div>
              <div className="text-xs lg:text-sm text-gray-400 space-y-1 mb-3">
                <p>
                  <span className="font-semibold text-gray-300">
                    Part No:
                  </span>{" "}
                  {item.partNumber}
                </p>
                <p>
                  <span className="font-semibold text-gray-300">
                    Make:
                  </span>{" "}
                  {item.make}
                </p>
                <p>
                  <span className="font-semibold text-gray-300">
                    Model:
                  </span>{" "}
                  {item.model}
                </p>
              </div>
              <div className="flex gap-2 mt-2">
                <button className="bg-blue-700 hover:bg-blue-600 text-white px-4 py-2 w-20 rounded-lg transition-colors">
                  Chat
                </button>
                <button className="bg-green-700 hover:bg-green-600 text-white px-4 py-2 w-20 rounded-lg transition-colors">
                  Call
                </button>
                <button className="bg-green-700 hover:bg-green-600 text-white px-4 py-2 w-64 rounded-lg transition-colors">
                  Get Distance From My Place
                </button>                
              </div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-gray-500 mt-4">
            No matching parts found. Try a broader search.
          </p>
        )}
      </div>
    );
  };

  const renderJugaadu = () => (
    <div className="mt-4 space-y-4">
      {/* List of Jugaad */}
      {!selectedJugaad && (
        <>
          {jugaadList.map((j) => (
            <div
              key={j.id}
              className="bg-neutral-900 text-gray-200 p-4 rounded-2xl shadow-md cursor-pointer border border-neutral-800"
              onClick={() => {
                setSelectedJugaad(j);
                setSelectedPart(null);
              }}
            >
              <h3 className="text-lg font-semibold">{j.title}</h3>
              <p className="text-xs text-gray-500 mt-1">{j.date}</p>
              <p className="text-sm mt-2 text-gray-400">{j.comments}</p>
            </div>
          ))}
        </>
      )}
      {selectedJugaad && !selectedPart && (
        <div>
          <button
            className="mb-4 border border-neutral-600 text-gray-200 px-4 py-2 rounded-lg hover:bg-neutral-800 transition-colors"
            onClick={() => setSelectedJugaad(null)}
          >
            ⬅ Back to all Jugaad
          </button>
          <h2 className="text-xl mb-4 font-semibold text-white">
            Parts I Need – {selectedJugaad.title}
          </h2>
          {selectedJugaad.parts.map((p) => (
            <div
              key={p}
              className="bg-neutral-900 text-gray-200 p-4 rounded-2xl shadow cursor-pointer border border-neutral-800 mb-3"
              onClick={() => setSelectedPart(p)}
            >
              <div className="p-0 text-sm lg:text-base">{p}</div>
            </div>
          ))}
        </div>
      )}
      {selectedPart && (
        <div>
          <button
            className="mb-4 border border-neutral-600 text-gray-200 px-4 py-2 rounded-lg hover:bg-neutral-800 transition-colors"
            onClick={() => setSelectedPart(null)}
          >
            ⬅ Back to parts list
          </button>
          <h2 className="text-xl mb-2 font-semibold text-white">
            {selectedPart}
          </h2>
          <div className="space-y-1 text-sm lg:text-base text-gray-300">
            <p>Description: Dummy description of {selectedPart}.</p>
            <p>Unit: 1</p>
            <p>Budget: ₹200</p>
            <p>Actual Cost: ₹180</p>
            <p>Status: Pending</p>
          </div>
          <h3 className="mt-4 font-semibold text-white">My Comments</h3>
          <ul className="list-disc pl-6 text-gray-400 text-sm lg:text-base">
            <li>Waiting for part availability.</li>
            <li>
              Will check nearby Kabaadi shops and Anything tab.
            </li>
          </ul>
          <button
            className="mt-4 bg-orange-700 hover:bg-orange-600 text-white px-4 py-2 rounded-lg transition-colors"
            onClick={() => setActiveTab("Anything")}
          >
            🔍 Search this part in Anything
          </button>
        </div>
      )}
    </div>
  );

  const renderKabaadi = () => {
    const filteredVendors = scrapVendors.filter((v) => {
      if (!vendorSearch.trim()) return true;
      const q = vendorSearch.toLowerCase();
      return (
        v.name.toLowerCase().includes(q) ||
        v.shop.toLowerCase().includes(q) ||
        v.city.toLowerCase().includes(q) ||
        v.state.toLowerCase().includes(q) ||
        v.location.toLowerCase().includes(q) ||
        v.pin.toLowerCase().includes(q)
      );
    });

    return (
      <div className="mt-4 space-y-4">
        <input
          placeholder="Search vendor by name, shop, city, state, pincode..."
          value={vendorSearch}
          onChange={(e) => setVendorSearch(e.target.value)}
          className="bg-neutral-800 text-gray-200 border border-neutral-700 text-sm w-full px-3 py-2 rounded-lg"
        />
        {filteredVendors.map((v) => (
          <div
            key={v.shop}
            className="bg-neutral-900 text-gray-200 p-4 rounded-2xl shadow border border-neutral-800"
          >
            <div className="p-0 space-y-1 text-sm lg:text-base">
              <h3 className="font-semibold text-lg text-white">{v.shop}</h3>
              <p className="text-gray-300">{v.name}</p>
              <p className="text-gray-400">
                {v.location}, {v.city}
              </p>
              <p className="text-gray-400">
                {v.state}, {v.country} - {v.pin}
              </p>
            </div>
          </div>
        ))}
        {filteredVendors.length === 0 && (
          <p className="text-sm text-gray-500">
            No matching scrap stockist found in dummy data.
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-gray-300 p-4">
      <div className="grid grid-cols-4 lg:grid-cols-8 gap-1 mb-1 place-items-center">
        {TABS.map((t) => (
          <motion.div
            key={t}
            whileTap={{ scale: 0.97 }}
            className="w-full"
          >
            <button
              className={`w-full rounded-md text-xs lg:text-sm py-2 lg:py-2.5 transition-all ${
                activeTab === t
                  ? "bg-blue-600 text-white"
                  : "bg-neutral-800 hover:bg-neutral-700 text-gray-200"
              }`}
              onClick={() => {
                if (t === "AI") {
                  window.location.href = "/agenticsim";
                  return;
                }
                setActiveTab(t);
                setSelectedJugaad(null);
                setSelectedPart(null);
              }}
            >
              {t}
            </button>
          </motion.div>
        ))}
      </div>

      {activeTab === "Why PuranaWala" && (
        <div className="space-y-4">
          <h1 className="text-xl lg:text-2xl font-bold mb-2 text-white">
            Why PuranaWala?
          </h1>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Text Section */}
            <div className="lg:w-2/3 space-y-3 text-sm lg:text-base leading-6">
              <p>
                <strong>Vision:</strong> To create India’s most trusted and
                intelligent gateway for sourcing old, rare, refurbished,
                second-hand, and scrap components—empowering every stakeholder in
                the repair, reuse, rebuild, and innovation ecosystem.
              </p>
              <p>
                <strong>Mission:</strong> To make the search, discovery,
                exchange, and utilisation of old parts effortless using structured
                data, community knowledge, and AI-driven assistance so that
                anyone—from a technician to an innovator—can solve problems
                faster and more affordably.
              </p>
              <p>
                <strong>About PuranaWala:</strong> PuranaWala is a dedicated
                module inside the Think5Whys ecosystem that helps users find old
                components needed during repair, retrofitting, prototyping,
                cost-saving, or jugaad-making. It bridges the gap between people
                who need parts and those who possess unused, idle, scrap or old
                components.
              </p>
              <div>
                <h2 className="font-semibold text-white mt-3">
                  Stakeholders & What They Can Do
                </h2>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-300">
                  <li>
                    <strong>Service Center Owners & Staff:</strong> Search old
                    components, reduce repair cost, accelerate service time,
                    negotiate availability, chat/call vendors, and track part
                    requirements.
                  </li>
                  <li>
                    <strong>Customers:</strong> Find affordable alternatives for
                    costly new parts, verify availability before visiting shops,
                    and explore repair possibilities.
                  </li>
                  <li>
                    <strong>Scrap Vendors:</strong> Showcase stock, list parts,
                    respond to chat/call requests, and sell old components
                    faster.
                  </li>
                  <li>
                    <strong>Innovators / Jugaadu Creators:</strong> Document
                    innovations, list part requirements, track costs, find
                    components across categories, and build faster prototypes.
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="font-semibold text-white mt-3">Benefits</h2>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-300">
                  <li>Reduces repair cost significantly.</li>
                  <li>Promotes circular economy & reduces waste.</li>
                  <li>Makes rare or discontinued parts discoverable.</li>
                  <li>
                    Boosts local economy by supporting repair shops and scrap
                    sellers.
                  </li>
                  <li>
                    Enables innovators to build quickly with low-budget
                    components.
                  </li>
                </ul>
              </div>
              <div>
                <h2 className="font-semibold text-white mt-3">
                  How PuranaWala AI Helps
                </h2>
                <ul className="list-disc ml-5 mt-2 space-y-1 text-gray-300">
                  <li>
                    Understands part descriptions even if incomplete or
                    informal.
                  </li>
                  <li>Recommends alternate compatible parts.</li>
                  <li>Predicts availability based on patterns.</li>
                  <li>Connects users to the right vendor instantly.</li>
                  <li>
                    Generates insights, price trends, stock alerts, and repair
                    suggestions.
                  </li>
                </ul>
              </div>
              <p className="mt-2 text-gray-300">
                <strong>Without AI:</strong> Users must rely on phone calls,
                manual enquiries, visiting multiple shops, and guesswork—
                resulting in time loss, higher expenses, and uncertainty. With
                PuranaWala AI, discovery becomes instant, structured, and
                effortless.
              </p>
            </div>
            <div className="lg:w-1/3 h-64 lg:h-72 bg-neutral-900 rounded-2xl border border-neutral-800 p-4">
              <h3 className="text-sm font-semibold mb-2 text-gray-200">
                Typical Old Parts Demand Mix
              </h3>
              <div className="w-full h-full">
                <Pie data={chartData} options={chartOptions} />
              </div>
            </div>
          </div>
        </div>
      )}

      {["Electronics", "Automobiles", "Fittings", "Anything"].includes(
        activeTab
      ) && (
        <div className="mt-2">
          <h2 className="text-lg lg:text-xl font-semibold mb-3 text-white">
            {activeTab} – Search Old Parts
          </h2>
          <input
            placeholder="Search item, part number, model, make..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-neutral-800 text-gray-200 border border-neutral-700 text-sm lg:text-base w-full px-3 py-2 rounded-lg"
          />
          {renderSearchResults()}
        </div>
      )}

      {activeTab === "Jugaadu" && renderJugaadu()}

      {activeTab === "Kabaadi" && renderKabaadi()}
    </div>
  );
}